import React, { Suspense, lazy, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes, useLocation, useParams } from "react-router-dom";
import { RootState } from "../app/store";
import LoadingSpinner from "../components/LoadingSpinner";
import { ROUTE_CONFIG } from "../core/config/routes";
import { getPostLoginPath, hasAnyPermission } from "../core/utils/permissions";
import PageLayout from "../layouts/PageLayout/PageLayout";
import R_Layout from "../layouts/PageLayout/R_Layout";
import Email_Verify from "../pages/Dashboard/Email_Verify";
import BulkVerificationResults from "../pages/Dashboard/BulkVerificationResults";
import EmailDetails from "../pages/Dashboard/EmailDetails";
import Integration from "../pages/Dashboard/Integration";
import ApiAccess from "../pages/Dashboard/ApiAccess";
import Pricing from "../pages/Dashboard/Pricing";
import ManageUsers from "../pages/Dashboard/ManageUsers";
import CreditLogs from "../pages/Dashboard/CreditLogs";
import VerificationLogs from "../pages/Dashboard/VerificationLogs";
import MyAccount from "../pages/Dashboard/MyAccount";
import PaymentHistory from "../pages/Dashboard/PaymentHistory";
import CreditBilling from "../pages/Dashboard/CreditBilling";
import Payment_Details from "../pages/Dashboard/Payment_Details";
import PaymentSuccess from "../pages/Dashboard/PaymentSuccess";
import PaymentCancel from "../pages/Dashboard/PaymentCancel";
import PaymentFailed from "../pages/Dashboard/PaymentFailed";
import R_ResetPassword from "../features/auth/components/R_ResetPassword";
import R_ForgotPassword from "../features/auth/components/R_ForgotPassword";
import R_ForgotPasswordVerify from "../features/auth/components/R_ForgotPasswordVerify";
import R_RegistrationOtpVerify from "../features/auth/components/R_RegistrationOtpVerify";
import { LinkedInCallback } from "react-linkedin-login-oauth2";

// Lazy-loaded components
const AuthLayout = lazy(() => import("../layouts/Authentication/AuthLayout"));
const LoginForm = lazy(() => import("../features/auth/components/R_Sign_In"));
const SignupForm = lazy(() => import("../features/auth/components/R_Sign_Up"));
const NotAuthorized = lazy(() => import("../pages/NotAuthorized"));
const EmailOperations = lazy(() => import("../features/email/components/EmailOperations"));
const UserManagement = lazy(() => import("../features/user/components/UserManagement"));
const NetBoxManagement = lazy(() => import("../features/server-ip-details/components/NetBoxManagement"));
const BillingManagement = lazy(() => import("../features/admin/pages/BillingManagement"));
const EmailSettings = lazy(() => import("../features/email-patterns/EmailSettings"));
const ServerIPViewPage = lazy(() => import("../features/server-ip-details/components/ServerIPViewPage"));
const ServerIPStatsPage = lazy(() => import("../features/server-ip-details/components/ServerIPStatsPage"));
const OvhFleetConnectionStatus = lazy(() => import("../features/server-ip-details/components/OvhFleetConnectionStatus"));
const SystemEventLogsPage = lazy(() => import("../features/system-event-logs/components/SystemEventLogsPage"));
const ViewCredits = lazy(() => import("../features/user/components/ViewCredits"));
const UserProfilePage = lazy(() => import("../features/user/components/UserProfilePage"));
const ErrorLogs = lazy(() => import("../features/error-log/ErrorLogs"));
const PageNotFound = lazy(() => import("../pages/PageNotFound"));
const Dashboard = lazy(() => import("../pages/Dashboard/Dashboard"));
const AdminOverview = lazy(() => import("../features/admin/pages/AdminOverview"));
const CreateCoupon = lazy(() => import("../features/coupon/CreateCoupon"));
const EditCoupon = lazy(() => import("../features/coupon/EditCoupon"));
const CouponUsers = lazy(() => import("../features/coupon/CouponUsers"));
const AppsumoActivate = lazy(() => import("../features/auth/components/AppsumoActivate"));
/**
 * Permission-protected route component
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode, requiredPermissions: string[] }> = ({
  children,
  requiredPermissions
}) => {
  const location = useLocation();
  const permissions = useSelector((state: RootState) => state.auth.permissions);
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.accessToken);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasAnyPermission(requiredPermissions, permissions)) {
    return <Navigate to="/not-authorized" state={{ from: location }} replace />;
  }

  return children;
};

// Legacy per-action user routes (e.g. /user-audit-logs/:userId) now live as
// tabs on the single UserProfilePage - redirect them there, carrying the
// :userId param along since Navigate's `to` can't interpolate it directly.
const RedirectToUserProfileTab: React.FC<{ tab: string }> = ({ tab }) => {
  const { userId } = useParams<{ userId: string }>();
  return <Navigate to={`/user-profile/${userId}?tab=${tab}`} replace />;
};

const BlockRoleRoute: React.FC<{ children: React.ReactNode, blockedRoleIds?: number[] }> = ({
  children,
  blockedRoleIds = [],
}) => {
  const location = useLocation();
  const roleId = useSelector((state: RootState) => state.auth.roleId);

  if (blockedRoleIds.includes(roleId ?? -1)) {
    return <Navigate to="/not-authorized" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// The bare domain root (ROUTE_CONFIG.PUBLIC.path) is what email links like
// "Generate API key" / "Verify your first emails" point to. It used to render
// LoginForm unconditionally, bouncing an already-authenticated visitor to
// sign-in for no reason — this sends them to their post-login landing page instead.
const PublicRoot: React.FC = () => {
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.accessToken);
  const roleId = useSelector((state: RootState) => state.auth.roleId);
  const permissions = useSelector((state: RootState) => state.auth.permissions);
  // ?next= is honoured here too, so the HubSpot Marketplace Install button can
  // use one URL for everybody: a signed-out visitor gets the form (which keeps
  // the parameter), a signed-in one goes straight there.
  const next = new URLSearchParams(useLocation().search).get("next");
  return isAuthenticated ? (
    <Navigate to={getPostLoginPath({ role_id: roleId, permissions }, next)} replace />
  ) : (
    <LoginForm />
  );
};

const AppRouter: React.FC = () => {
  useEffect(() => {
    // Add dropdown menu CSS
    const dropdownCssLink = document.createElement('link');
    dropdownCssLink.href = '/css/dropdown-menu.css';
    dropdownCssLink.rel = 'stylesheet';
    dropdownCssLink.id = 'dropdown-menu-css';
    document.head.appendChild(dropdownCssLink);

    return () => {
      const dropdownCssLink = document.getElementById('dropdown-menu-css');
      if (dropdownCssLink) dropdownCssLink.remove();

      const auditLogsCssLink = document.getElementById('user-audit-logs-css');
      if (auditLogsCssLink) auditLogsCssLink.remove();

      const adminAuditLogsCssLink = document.getElementById('admin-audit-logs-css');
      if (adminAuditLogsCssLink) adminAuditLogsCssLink.remove();

      const netboxEventErrorsCssLink = document.getElementById('netbox-event-errors-css');
      if (netboxEventErrorsCssLink) netboxEventErrorsCssLink.remove();
    };
  }, []);

  return (
    <Router>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route element={<AuthLayout><Outlet /></AuthLayout>}>
            <Route path={ROUTE_CONFIG.PUBLIC.path} element={<PublicRoot />} />
            <Route path={ROUTE_CONFIG.LOGIN.path} element={<LoginForm />} />
            <Route path={ROUTE_CONFIG.SIGNUP_WITH_PLAN.path} element={<SignupForm />} />
            <Route path="/forgot-password" element={<R_ForgotPassword />} />
            <Route path="/forgot-password/verify" element={<R_ForgotPasswordVerify />} />
            <Route path="/activate" element={<R_ResetPassword />} />
            <Route path="/register/verify-otp" element={<R_RegistrationOtpVerify />} />
            {/* AppSumo OAuth activation — public: the buyer isn't logged in yet
                when the backend redirects them here (see appsumo_oauth_callback
                in the backend), so this must stay outside ProtectedRoute. */}
            <Route path="/appsumo/activate" element={<AppsumoActivate />} />
          </Route>

          {/* Protected Routes */}
          <Route element={<PageLayout><Outlet /></PageLayout>}>


          
            {/* Admin Overview - the admin panel's landing page */}
            <Route path={ROUTE_CONFIG.ADMIN_OVERVIEW.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.ADMIN_OVERVIEW.permissions}>
                <AdminOverview />
              </ProtectedRoute>
            } />

            {/* Coupons - legacy link, now a tab inside Plans & Billing */}
            <Route path={ROUTE_CONFIG.COUPONS_LIST.path} element={<Navigate to="/billing-management?tab=coupons" replace />} />

            <Route path={ROUTE_CONFIG.CREATE_COUPON.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.CREATE_COUPON.permissions}>
                <CreateCoupon />
              </ProtectedRoute>
            } />

            <Route path={ROUTE_CONFIG.EDIT_COUPON.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.EDIT_COUPON.permissions}>
                <EditCoupon />
              </ProtectedRoute>
            } />

            <Route path={ROUTE_CONFIG.COUPON_USAGE.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.COUPON_USAGE.permissions}>
                <CouponUsers />
              </ProtectedRoute>
            } />

            {/* User Management - single tabbed page (Users List / User Roles / Audit Logs) */}
            <Route path={ROUTE_CONFIG.USER_MANAGEMENT.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.USER_MANAGEMENT.permissions}>
                <UserManagement />
              </ProtectedRoute>
            } />

            {/* Legacy User Management links - redirect to the corresponding tab */}
            <Route path={ROUTE_CONFIG.USERS_LIST.path} element={<Navigate to="/user-management?tab=paid-users" replace />} />
            <Route path={ROUTE_CONFIG.USER_ROLES.path} element={<Navigate to="/user-management?tab=user-roles" replace />} />
            <Route path={ROUTE_CONFIG.ADMIN_AUDIT_LOGS.path} element={<Navigate to="/user-management?tab=audit-logs" replace />} />
            <Route path={ROUTE_CONFIG.USER_CREDITS.path} element={<Navigate to="/user-management?tab=credit-reports" replace />} />

            {/* Email Settings - single tabbed page (Email Patterns / Cache Settings) */}
            <Route path={ROUTE_CONFIG.EMAIL_SETTINGS.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.EMAIL_SETTINGS.permissions}>
                <EmailSettings />
              </ProtectedRoute>
            } />
            <Route path={ROUTE_CONFIG.EMAIL_PATTERNS.path} element={<Navigate to="/email-settings?tab=email-patterns" replace />} />

            {/* NetBox Management - single tabbed page (Server Lists / Server IPs / Monitoring / Event Logs / Blocked IPs) */}
            <Route path={ROUTE_CONFIG.NETBOX_MANAGEMENT.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.NETBOX_MANAGEMENT.permissions}>
                <NetBoxManagement />
              </ProtectedRoute>
            } />

            {/* Legacy NetBox links - redirect to the corresponding tab */}
            <Route path={ROUTE_CONFIG.SERVER_IP_LIST.path} element={<Navigate to="/netbox-management?tab=server-ips" replace />} />
            <Route path={ROUTE_CONFIG.SERVER_IP_DETAILS.path} element={<Navigate to="/netbox-management?tab=server-lists" replace />} />
            <Route path={ROUTE_CONFIG.NETBOX_EVENT_ERRORS.path} element={<Navigate to="/netbox-management?tab=event-logs" replace />} />
            <Route path={ROUTE_CONFIG.BLOCKED_IPS.path} element={<Navigate to="/netbox-management?tab=blocked-ips" replace />} />
            <Route path={ROUTE_CONFIG.EMAIL_VERIFIER_STATS.path} element={<Navigate to="/netbox-management?tab=email-verifier-stats" replace />} />

            {/* Monitoring - kept as its own standalone page/menu item */}
            <Route path={ROUTE_CONFIG.SERVER_IP_STATS_OVERALL.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.SERVER_IP_STATS_OVERALL.permissions}>
                <ServerIPStatsPage />
              </ProtectedRoute>
            } />

            {/* OVH Fleet Connection Status - its own standalone page/menu item */}
            <Route path={ROUTE_CONFIG.OVH_FLEET_STATUS.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.OVH_FLEET_STATUS.permissions}>
                <OvhFleetConnectionStatus />
              </ProtectedRoute>
            } />

            {/* Server-specific drill-down pages (keep :id-based routes standalone) */}
            <Route path={ROUTE_CONFIG.SERVER_IP_VIEW.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.SERVER_IP_VIEW.permissions}>
                <ServerIPViewPage />
              </ProtectedRoute>
            } />

            <Route path={ROUTE_CONFIG.SERVER_IP_STATS.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.SERVER_IP_STATS.permissions}>
                <ServerIPStatsPage />
              </ProtectedRoute>
            } />

            <Route path={ROUTE_CONFIG.SYSTEM_EVENT_LOGS.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.SYSTEM_EVENT_LOGS.permissions}>
                <SystemEventLogsPage />
              </ProtectedRoute>
            } />

            {/* API History - legacy link, now a tab inside Email Operations */}
            <Route path={ROUTE_CONFIG.API_HISTORY.path} element={<Navigate to="/email-operations?tab=api-history" replace />} />

            {/* Plans & Billing - single tabbed page (Plans / Payment History / Coupons) */}
            <Route path={ROUTE_CONFIG.BILLING_MANAGEMENT.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.BILLING_MANAGEMENT.permissions}>
                <BillingManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/plans/:id?" element={<Navigate to="/billing-management?tab=plans" replace />} />
            <Route path="/all-payment-history" element={<Navigate to="/billing-management?tab=payment-history" replace />} />

            {/* HubSpot Integrations - legacy link, now a tab inside Email Operations */}
            <Route path={ROUTE_CONFIG.HUBSPOT_INTEGRATIONS.path} element={<Navigate to="/email-operations?tab=hubspot-integrations" replace />} />

            {/* Email Operations - single tabbed page (Bulk Verify / Bulk Finder / Verify Email / Email Finder) */}
            <Route path={ROUTE_CONFIG.EMAIL_OPERATIONS.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.EMAIL_OPERATIONS.permissions}>
                <EmailOperations />
              </ProtectedRoute>
            } />

            {/* Legacy Email Operations links - redirect to the corresponding tab */}
            <Route path={ROUTE_CONFIG.BULK_EMAIL_VERIFY.path} element={<Navigate to="/email-operations?tab=bulk-verify" replace />} />
            <Route path={ROUTE_CONFIG.BULK_EMAIL_FINDER.path} element={<Navigate to="/email-operations?tab=bulk-finder" replace />} />
            <Route path={ROUTE_CONFIG.VERIFY_EMAIL.path} element={<Navigate to="/email-operations?tab=verify-email" replace />} />
            <Route path={ROUTE_CONFIG.EMAIL_FINDER.path} element={<Navigate to="/email-operations?tab=email-finder" replace />} />

            {/* Logs */}
            <Route path={ROUTE_CONFIG.LOGS_HISTORY.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.LOGS_HISTORY.permissions}>
                <ErrorLogs />
              </ProtectedRoute>
            } />

            {/* User Profile - single tabbed page (Overview / Attribution / Audit Logs /
                Payment History / File Reports / Manage Credits / Credit History / Switch Plan / Edit) */}
            <Route path={ROUTE_CONFIG.USER_PROFILE.path} element={
              <ProtectedRoute requiredPermissions={ROUTE_CONFIG.USER_PROFILE.permissions}>
                <UserProfilePage />
              </ProtectedRoute>
            } />

            {/* Legacy per-action user links - redirect to the corresponding profile tab */}
            <Route path="/user-audit-logs/:userId" element={<RedirectToUserProfileTab tab="audit-logs" />} />
            <Route path="/user-payment-history/:userId" element={<RedirectToUserProfileTab tab="payment-history" />} />
            <Route path="/user-reports/:userId" element={<RedirectToUserProfileTab tab="file-reports" />} />
            <Route path="/credit-history/:userId" element={<RedirectToUserProfileTab tab="credit-history" />} />

            {/* Additional nested routes */}
            <Route path="/view-credit-history/:userId" element={
              <ProtectedRoute requiredPermissions={['users.view_credits']}>
                <ViewCredits />
              </ProtectedRoute>
            } />

            {/* Customer dashboard routes */}
            <Route
              element={
                <ProtectedRoute requiredPermissions={[]}>
                  <R_Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path={ROUTE_CONFIG.EMAIL_VERIFIER.path} element={<Email_Verify />} />
              <Route path={ROUTE_CONFIG.EMAIL_VERIFIER_RESULTS.path} element={<BulkVerificationResults />} />
              <Route path={ROUTE_CONFIG.EMAIL_VERIFIER_EMAIL_DETAILS.path} element={<EmailDetails />} />
              <Route
                path="/pricing"
                element={
                  <BlockRoleRoute blockedRoleIds={[6]}>
                    <Pricing />
                  </BlockRoleRoute>
                }
              />
              <Route path="/manage_users" element={<ManageUsers />} />
              <Route
                path="/credit-logs"
                element={
                  <BlockRoleRoute blockedRoleIds={[6]}>
                    <CreditLogs />
                  </BlockRoleRoute>
                }
              />
              <Route path="/verification-logs" element={<VerificationLogs />} />
              <Route path="/my-account" element={<MyAccount />} />
              <Route path="/payment-history" element={<PaymentHistory />} />
              <Route
                path="/credit-billing"
                element={
                  <BlockRoleRoute blockedRoleIds={[6]}>
                    <CreditBilling />
                  </BlockRoleRoute>
                }
              />
              <Route path="/integration" element={<Integration />} />
              <Route path="/api-access" element={<ApiAccess />} />
            </Route>
          </Route>



          {/* Payment Details - standalone without layout */}
          <Route path="/payment-details" element={
            <ProtectedRoute requiredPermissions={[]}>
              <Payment_Details />
            </ProtectedRoute>
          } />

          {/* LinkedIn OAuth callback */}
          <Route path="/linkedin" element={<LinkedInCallback />} />

           {/* Stripe checkout return URLs - standalone without layout */}
          <Route path="/payment/success" element={
            <ProtectedRoute requiredPermissions={[]}>
              <PaymentSuccess />
            </ProtectedRoute>
          } />
          <Route path="/payment/cancel" element={
            <ProtectedRoute requiredPermissions={[]}>
              <PaymentCancel />
            </ProtectedRoute>
          } />
          <Route path="/payment/failed" element={
            <ProtectedRoute requiredPermissions={[]}>
              <PaymentFailed />
            </ProtectedRoute>
          } />

          {/* Not Authorized */}
          <Route path={ROUTE_CONFIG.NOT_AUTHORIZED.path} element={<NotAuthorized />} />

          {/* 404 - Not Found */}
          <Route path="*" element={<PageNotFound />} />



        </Routes>

      </Suspense>
    </Router>
  );
};

export default AppRouter;
